import React, { useState, useRef, useEffect } from 'react';
import { Input } from './Input';

const OTPInput = ({ 
  length = 6, 
  onChange, 
  onComplete, 
  autoFocus = true,
  error = null,
  isLoading = false 
}) => {
  const [otp, setOtp] = useState(Array(length).fill(''));
  const inputRefs = useRef([]);

  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  const handleChange = (value, index) => {
    if (/[^0-9]/.test(value)) return; // Only allow numbers

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Move to next input if value is entered and not the last input
    if (value && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Call onChange with current OTP
    onChange && onChange(newOtp.join(''));

    // Check if OTP is complete
    if (newOtp.every(digit => digit !== '') && newOtp.length === length) {
      onComplete && onComplete(newOtp.join(''));
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      // Move to previous input when backspace is pressed on empty field
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e, index) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text/plain').replace(/\D/g, '');
    
    if (pasteData.length >= length) {
      const pastedOtp = pasteData.substring(0, length);
      const newOtp = pastedOtp.split('');
      setOtp(newOtp);
      onChange && onChange(newOtp.join(''));
      
      if (newOtp.every(digit => digit !== '') && newOtp.length === length) {
        onComplete && onComplete(newOtp.join(''));
      }
    } else {
      // Distribute pasted digits across inputs starting from current index
      let pasteIndex = 0;
      const newOtp = [...otp];
      
      for (let i = index; i < length && pasteIndex < pasteData.length; i++) {
        newOtp[i] = pasteData[pasteIndex];
        pasteIndex++;
      }
      
      setOtp(newOtp);
      onChange && onChange(newOtp.join(''));
      
      // Focus on the next empty field or last field
      const nextFocusIndex = Math.min(index + pasteData.length, length - 1);
      inputRefs.current[nextFocusIndex]?.focus();
    }
  };

  const clear = () => {
    const emptyOtp = Array(length).fill('');
    setOtp(emptyOtp);
    onChange && onChange('');
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-center gap-3">
        {otp.map((digit, index) => (
          <Input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(e.target.value, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            className={`w-12 h-14 text-center text-xl font-bold rounded-lg ${
              digit ? 'bg-accent' : 'bg-background'
            }`}
            disabled={isLoading}
            aria-label={`Digit ${index + 1} of ${length}`}
          />
        ))}
      </div>
      {error && (
        <p className="text-sm text-red-600 mt-1 text-center">{error}</p>
      )}
      <div className="flex justify-center">
        <button
          type="button"
          onClick={clear}
          className="text-xs text-muted-foreground hover:text-foreground transition-micro"
        >
          Clear
        </button>
      </div>
    </div>
  );
};

export default OTPInput;
